import colorUtil from 'color'

import appConfig from '~/src/App/config'
import getGlobal from '~/src/Lib/getGlobal'
import createLogger from '~/src/Lib/Logging'
import safeStorage from '~/src/Lib/safeStorage'
import { EMPTY_OBJECT } from '~/src/Lib/Utils'
import { shallowEqualsMemoizer } from '~/src/Lib/Utils/memoizer'

const logger = createLogger('Theme/color')

export const inputStates = ['active', 'disabled', 'enabled', 'focused', 'hovered']
export const states = {
  active: 0.16,
  disabled: 0.32,
  get disabledBackground() {
    return this.enabled / 2
  },
  enabled: 0.08,
  focused: 0.16,
  get hover() {
    return this.active
  },
  get hovered() {
    return this.active
  },
  inactive: 0.54,
  muted: 0.68,
  strong: 0.85,
}

export const getInputColorStates = shallowEqualsMemoizer((palette, keys = inputStates) => {
  const main = colorUtil(palette?.main ?? palette)

  return keys.reduce(
    (colorStates, key) => (key in states ? { ...colorStates, [key]: main.alpha(states[key]).string() } : colorStates),
    {},
  )
})
/**
 * @param {string} main The default color
 * @param {string} [defaultColorKey='main'] The key to use for the default color. Default is `'main'`
 * @param {object} [colorStates=states] State opacities object. Default is `states`
 * @param {object} [spread={}] Additional properties to spread onto the returned object. Default is `{}`
 * @returns
 */
export const getStringableColorWithStates = (
  main,
  defaultColorKey = 'main',
  colorStates = states,
  spread = EMPTY_OBJECT,
) => {
  if (!main) return { main }

  const proto = {
    ...spread,
    main,
    ...getInputColorStates(main, Object.keys(colorStates)),
  }

  const valueConfig = {
    value: () => proto[defaultColorKey],
  }
  Object.defineProperties(proto, {
    [Symbol.for('defaultColor')]: {
      value: defaultColorKey,
    },
    length: {
      get() {
        return main.length
      },
    },
    toString: valueConfig,
    valueOf: valueConfig,
  })

  return proto
}

const white = getStringableColorWithStates('#fff', 'strong')

const black = getStringableColorWithStates('#000', 'strong')
const blue = Object.freeze(
  getStringableColorWithStates('#338edc', 'main', states, {
    ...getInputColorStates('#338edc'),
    light: '#82c6ff',
    lighter: '#57a8ef',
    darker: '#2376bb',
    dark: '#0d63a7',
  }),
)

export const monochrome = Array.from({ length: 16 }, (_, i) => {
  const value = (15 - i).toString(16)
  return `#${value}${value}${value}`
})

const entityColors = {
  ipm: getStringableColorWithStates('#ffad8a'),
}

const itemColors = {
  denim: {
    dark: '#21527b',
    main: '#316c9d',
  },
  fig: {
    main: '#573a55',
    light: '#7d607c',
  },
  moss: {
    main: '#354e23',
    light: '#546845',
  },
  royal: {
    main: '#25377a',
    light: '#4857a1',
  },
  stone: {
    dark: '#2b3855',
    main: '#6c5c68',
    light: '#8f828c',
  },
  tan: {
    main: '#15473a',
    light: '#37645b',
  },
  terracotta: {
    main: '#78522a',
    light: '#826a53',
  },
  lights: {
    main: '#a0b10e',
  },
}

const batch = getStringableColorWithStates('#1ba293', 'main', states, {
  one: '#3aeae3',
  two: '#32d8ce',
  three: '#2bc6ba',
  four: '#23b4a6',
  five: '#1ba293',
  six: '#129180',
  seven: '#09806d',
  eight: '#00705b',
  light: '#a6d2cd',
})

const yellow = getStringableColorWithStates('#ffbf4d', 'main', states)

export const shared = {
  batch,
  blue,
  green: getStringableColorWithStates(
    '#89e05d',
    'main',
    {},
    {
      four: '#64c133',
      five: '#478924',
    },
  ),
  // Assistant brand orange. Brighter than `orange`, which AirTemp and others
  // use — this is only for AROYA assistant surfaces.
  assistant: getStringableColorWithStates('#ff8b2c', 'main'),
  orange: getStringableColorWithStates('#ff6a13', 'main'),
  purple: getStringableColorWithStates(
    '#a37b9a',
    'main',
    {},
    {
      five: '#7e5475',
    },
  ),
  yellow,
  states,
  black,
  white,
  monochrome,
  error: getStringableColorWithStates('#db2e4c', 'main', states, {
    notification: '#db2e4c',
    warning: yellow.main,
  }),
  info: blue,
  ec: getStringableColorWithStates('#ffbf4d'),
  soilTemp: getStringableColorWithStates('#ff975a'),
  steeringGen: getStringableColorWithStates('#6C27E7'),
  steeringVeg: getStringableColorWithStates('#2F9759'),
  success: batch,
  vwc: blue,
  warning: yellow,
}

export const palettes = {
  DEFAULT: Object.freeze({
    ...shared,
    ...entityColors,
    type: 'dark',
    get appPrimary() {
      return this.primary
    },
    get appSecondary() {
      return this.secondary
    },
    primary: {
      ...shared.blue,
      contrastText: shared.white.strong,
    },
    secondary: {
      ...batch,
      dark: batch.seven,
      faded: 'rgba(58, 234, 227, 0.12)',
      contrastText: shared.white.strong,
    },
    background: {
      default: '#071638',
      secondary: '#314153',
      paper: '#1b242f',
      dark: '#06122f',
    },
    contrast: white,
    contrastInverse: black,
    divider: white.active,
    gray: white.inactive,
    plantscore: {
      one: '#db1f40',
      two: '#ce4618',
      three: '#c08813',
      four: '#a0b10e',
      five: '#51a209',
      six: '#0b9205',
      seven: '#028133',
      eight: '#00705b',
    },
    ...Object.entries(itemColors).reduce(
      (colorMap, [name, { main, ...rest }]) => ({
        ...colorMap,
        [name]: getStringableColorWithStates(main, 'main', states, rest),
      }),
      {},
    ),
    text: {
      disabled: white.disabled,
      inactive: white.inactive,
      primary: white.strong,
      secondary: white.inactive,
    },
  }),
  get AROYA() {
    return this.DEFAULT
  },
}
if (safeStorage('local').getItem('debug') === 'true' && appConfig.ENVIRONMENT !== 'production') {
  getGlobal().palettes = palettes
  getGlobal().colorUtil = colorUtil
}
